/**
 * Enquiries page — Stage 1 of the Client Messages & Enquiries spec.
 *
 * Layout:
 *   [ Counters row ] [ Filters + saved views ] [ + New enquiry ]
 *   [ Search bar ]
 *   [ Two-pane split ] left: enquiry list, right: EnquiryDetail
 *
 * All heavy dialogs (fast entry / resolve / activity / edit) live in
 * ./components/enquiries/*.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  MessageCircle, Plus, Loader2, Search, Inbox, AlertCircle, Clock, UserX,
  ClipboardList, Settings as SettingsIcon, BarChart3, Facebook, CalendarClock,
} from "lucide-react";
import { NextActionLine } from "@/components/enquiries/NextAction";
import EnquiryQuickForm from "@/components/enquiries/EnquiryQuickForm";
import EnquiryDetail, { statusLabel } from "@/components/enquiries/EnquiryDetail";
import EnquiryCategoryManager from "@/components/enquiries/EnquiryCategoryManager";
import { CustomerStatusBadge, CustomerStatusChips } from "@/components/enquiries/CustomerStatus";
import { Inbox as InboxIcon } from "lucide-react";


const STATUS_TONES = {
  new: "bg-blue-50 text-blue-800 border-blue-200",
  assigned: "bg-purple-50 text-purple-800 border-purple-200",
  in_progress: "bg-amber-50 text-amber-900 border-amber-200",
  waiting_client: "bg-orange-50 text-orange-900 border-orange-200",
  waiting_internal: "bg-orange-50 text-orange-900 border-orange-200",
  scheduled: "bg-teal-50 text-teal-800 border-teal-200",
  resolved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  closed_no_action: "bg-zinc-100 text-zinc-600 border-zinc-200",
};

const PRIORITY_TONES = {
  low: "text-zinc-500",
  normal: "text-zinc-700",
  high: "text-amber-700 font-semibold",
  urgent: "text-red-700 font-bold",
};

const SAVED_VIEWS = [
  { key: "mine_open",          label: "My open",         icon: Inbox },
  { key: "unassigned",         label: "Unassigned",      icon: UserX },
  { key: "overdue",            label: "Overdue",         icon: AlertCircle },
  { key: "follow_ups",         label: "Follow-ups due",  icon: CalendarClock },
  { key: "waiting_client",     label: "Waiting on client", icon: Clock },
  { key: "new_this_week",      label: "New this week",   icon: MessageCircle },
];


export default function Enquiries() {
  const { user } = useAuth();
  const isManager = user && ["owner", "manager"].includes(user.role);
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [counters, setCounters] = useState({});
  const [inboxPending, setInboxPending] = useState(0);
  const [socialUnread, setSocialUnread] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showCategoryMgr, setShowCategoryMgr] = useState(false);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState(params.get("q") || "");

  const view = params.get("view") || "";
  const custStatus = params.get("cs") || "";
  const [csCounts, setCsCounts] = useState({});
  const deepLinkOpen = params.get("open");

  useEffect(() => {
    if (deepLinkOpen) setSelected(deepLinkOpen);
  }, [deepLinkOpen]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const listParams = {};
      if (view) listParams.view = view;
      if (q.trim()) listParams.q = q.trim();
      const [rList, rCat, rCounters, rCs] = await Promise.all([
        api.get("/enquiries", { params: custStatus ? { ...listParams, customer_status: custStatus } : listParams }),
        api.get("/enquiry-categories"),
        api.get("/enquiries/counters"),
        api.get("/enquiries/customer-status-counts", { params: listParams }),
      ]);
      setRows(rList.data || []);
      setCsCounts(rCs.data || {});
      setCategories(rCat.data || []);
      setCounters(rCounters.data || {});
      // Fire-and-forget: pull the inbox pending counter so we can badge
      // the button. Don't block the main list on this.
      api.get("/enquiry-inbox", { params: { status: "pending", limit: 1 } })
        .then((r) => setInboxPending(r.data?.counters?.pending || 0))
        .catch(() => {});
      // Fetch Social Inbox unread badge in parallel.
      api.get("/social/conversations", { params: { unread_only: true, limit: 1 } })
        .then((r) => setSocialUnread(r.data?.unread_total || 0))
        .catch(() => {});
    } catch (e) {
      toast.error("Load failed", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, [view, q, custStatus]);

  useEffect(() => { load(); }, [load]);

  function setParam(key, v) {
    const next = new URLSearchParams(params);
    if (v) next.set(key, v); else next.delete(key);
    setParams(next, { replace: true });
  }
  const setView = (v) => setParam("view", v);

  function onCreated(newEnq) {
    setShowCreate(false);
    load();
    if (newEnq?.id) setSelected(newEnq.id);
  }

  const groupedCategories = useMemo(() => {
    const g = {};
    for (const c of categories) {
      const key = c.group || "Other";
      g[key] = g[key] || [];
      g[key].push(c);
    }
    return g;
  }, [categories]);

  return (
    <div className="max-w-[1400px] mx-auto p-4 space-y-4" data-testid="enquiries-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-950 flex items-center gap-2">
            <MessageCircle className="h-6 w-6" /> Enquiries
          </h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            Every enquiry, message and request in one accountable register.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/enquiries/inbox">
            <Button
              variant="outline"
              className="text-xs relative"
              data-testid="enquiries-inbox-btn"
            >
              <InboxIcon className="h-3.5 w-3.5 mr-1" /> Website inbox
              {inboxPending > 0 && (
                <span
                  className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-4 rounded-full px-1 text-2xs font-semibold bg-amber-700 text-white"
                  data-testid="enquiries-inbox-pending-badge"
                >
                  {inboxPending}
                </span>
              )}
            </Button>
          </Link>
          <Link to="/enquiries/social">
            <Button
              variant="outline"
              className="text-xs relative"
              data-testid="enquiries-social-btn"
            >
              <Facebook className="h-3.5 w-3.5 mr-1" /> Social inbox
              {socialUnread > 0 && (
                <span
                  className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-4 rounded-full px-1 text-2xs font-semibold bg-indigo-600 text-white"
                  data-testid="enquiries-social-unread-badge"
                >
                  {socialUnread}
                </span>
              )}
            </Button>
          </Link>
          {isManager && (
            <Link to="/enquiries/analytics">
              <Button
                variant="outline"
                className="text-xs"
                data-testid="enquiries-analytics-btn"
              >
                <BarChart3 className="h-3.5 w-3.5 mr-1" /> Analytics
              </Button>
            </Link>
          )}
          {isManager && (
            <Button
              variant="outline"
              onClick={() => setShowCategoryMgr(true)}
              className="text-xs"
              data-testid="enquiries-manage-categories-btn"
            >
              <SettingsIcon className="h-3.5 w-3.5 mr-1" /> Categories
            </Button>
          )}
          <Button
            onClick={() => setShowCreate(true)}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="enquiries-new-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> New enquiry
          </Button>
        </div>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="enquiries-counters">
        <Counter label="My open" value={counters.mine_open || 0} onClick={() => setView("mine_open")} testId="counter-mine-open" />
        <Counter label="My overdue" value={counters.mine_overdue || 0} onClick={() => setView("overdue")} tone="red" testId="counter-mine-overdue" />
        <Counter label="Unassigned" value={counters.unassigned || 0} onClick={() => setView("unassigned")} tone="amber" testId="counter-unassigned" />
        <Counter label="Urgent" value={counters.urgent_open || 0} tone="red" testId="counter-urgent" />
        <Counter label="Waiting on client" value={counters.waiting_client || 0} onClick={() => setView("waiting_client")} tone="orange" testId="counter-waiting-client" />
        <Counter label="New (24h)" value={counters.new_today || 0} testId="counter-new-today" />
      </div>

      {/* Saved views + search */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setView("")}
          className={`text-xs px-2 py-1 rounded-md border ${!view ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-300"}`}
          data-testid="enquiries-view-all"
        >All</button>
        {SAVED_VIEWS.map((v) => (
          <button
            key={v.key}
            onClick={() => setView(v.key)}
            className={`text-xs px-2 py-1 rounded-md border inline-flex items-center gap-1 ${view === v.key ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-300"}`}
            data-testid={`enquiries-view-${v.key}`}
          >
            <v.icon className="h-3 w-3" /> {v.label}
            {v.key === "follow_ups" && counters.follow_ups_due > 0 && (
              <span className="ml-0.5 rounded-full bg-amber-700 px-1.5 text-2xs font-semibold text-white" data-testid="enquiries-follow-ups-count">
                {counters.follow_ups_due}</span>
            )}
          </button>
        ))}
        <div className="relative flex-1 min-w-[240px] ml-auto">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-zinc-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search subject, message, name, phone, email…"
            className="pl-8"
            data-testid="enquiries-search"
          />
        </div>
      </div>

      <CustomerStatusChips value={custStatus} counts={csCounts} onChange={(v) => setParam("cs", v)} />

      {/* Split pane */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-4">
        <div className="min-h-[400px]">
          {loading ? (
            <div className="p-6 text-sm text-zinc-500 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center border border-dashed border-zinc-200 rounded-md text-sm text-zinc-500" data-testid="enquiries-empty">
              No enquiries match.
            </div>
          ) : (
            <ul className="space-y-2 max-h-[calc(100vh-320px)] overflow-y-auto pr-1" data-testid="enquiries-list">
              {rows.map((e) => (
                <EnquiryRow
                  key={e.id}
                  enq={e}
                  active={selected === e.id}
                  onClick={() => setSelected(e.id)}
                />
              ))}
            </ul>
          )}
        </div>
        <div>
          {selected ? (
            <EnquiryDetail
              enquiryId={selected}
              categories={categories}
              categoryGroups={groupedCategories}
              onChanged={load}
              onClose={() => setSelected(null)}
              onSelect={setSelected}
            />
          ) : (
            <Card className="p-10 rounded-md shadow-none border-2 border-dashed border-zinc-200 bg-zinc-50 text-center text-sm text-zinc-500" data-testid="enquiries-detail-empty">
              <ClipboardList className="h-8 w-8 mx-auto text-zinc-400 mb-2" />
              Pick an enquiry from the list to see full detail.
            </Card>
          )}
        </div>
      </div>

      {showCreate && (
        <EnquiryQuickForm
          categories={categories}
          categoryGroups={groupedCategories}
          onClose={() => setShowCreate(false)}
          onCreated={onCreated}
        />
      )}

      {showCategoryMgr && (
        <EnquiryCategoryManager
          onClose={() => setShowCategoryMgr(false)}
          onChanged={load}
        />
      )}
    </div>
  );
}


function Counter({ label, value, onClick, tone = "zinc", testId }) {
  const toneCls = {
    zinc: "border-zinc-200 bg-white",
    red: value > 0 ? "border-red-300 bg-red-50" : "border-zinc-200 bg-white",
    amber: value > 0 ? "border-amber-300 bg-amber-50" : "border-zinc-200 bg-white",
    orange: value > 0 ? "border-orange-300 bg-orange-50" : "border-zinc-200 bg-white",
  }[tone] || "border-zinc-200 bg-white";
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className={`p-3 rounded-md border ${toneCls} text-left ${onClick ? "hover:border-zinc-400 cursor-pointer" : "cursor-default"}`}
      data-testid={testId}
    >
      <div className="text-2xl font-semibold text-zinc-950 font-mono-num">{value}</div>
      <div className="text-2xs uppercase tracking-wider text-zinc-600 mt-0.5">{label}</div>
    </button>
  );
}


function EnquiryRow({ enq, active, onClick }) {
  const status = enq.status || "new";
  const priority = enq.priority || "normal";
  const overdue = enq.due_at && ["new", "assigned", "in_progress", "waiting_client", "waiting_internal", "scheduled"].includes(status) && new Date(enq.due_at) < new Date();
  const contact = enq.contact || {};
  return (
    <li>
      <button
        onClick={onClick}
        className={`w-full text-left border rounded-md p-3 transition-colors ${active ? "border-zinc-950 bg-zinc-50" : "border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50"}`}
        data-testid={`enquiry-row-${enq.id}`}
      >
        <div className="flex items-start gap-2 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-zinc-950 truncate">{enq.subject}</div>
            <div className="text-2xs text-zinc-500 mt-0.5 truncate">
              {contact.name || (contact.unknown ? "Unknown caller" : contact.email || contact.mobile || "—")}
              {!contact.unknown && <CustomerStatusBadge status={enq.customer_status} className="ml-1" testId={`enquiry-row-status-${enq.id}`} />}
              {enq.category_name && <span> · {enq.category_name}</span>}
            </div>
            <div className="text-2xs text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
              <span className={`uppercase tracking-wider px-1 py-0.5 rounded border text-2xs ${STATUS_TONES[status] || ""}`}>
                {statusLabel(status)}
              </span>
              <span className={`uppercase text-2xs ${PRIORITY_TONES[priority] || ""}`}>{priority}</span>
              {enq.source_channel === "meta_lead_ad" && (
                <span
                  className="uppercase tracking-wider px-1 py-0.5 rounded border text-2xs bg-blue-50 text-blue-800 border-blue-200"
                  data-testid={`enquiry-source-pill-${enq.id}`}
                  title={
                    enq.marketing_attribution?.form_name
                      ? `${enq.platform === "instagram" ? "Instagram" : "Facebook"} Lead Ad — ${enq.marketing_attribution.form_name}`
                      : "Meta Lead Ad"
                  }
                >
                  {enq.platform === "instagram" ? "IG lead ad"
                    : enq.platform === "facebook" ? "FB lead ad"
                    : "Meta lead ad"}
                </span>
              )}
              {overdue && (
                <span className="text-2xs uppercase text-red-700 font-semibold inline-flex items-center gap-0.5">
                  <AlertCircle className="h-3 w-3" /> overdue
                </span>
              )}
              <span className="ml-auto" title={`Received ${new Date(enq.created_at).toLocaleString()}`} data-testid={`enquiry-row-date-${enq.id}`}>
                {new Date(enq.created_at).toLocaleDateString()}
              </span>
            </div>
            <NextActionLine enq={enq} />
          </div>
        </div>
      </button>
    </li>
  );
}
